/**
 * Shells.js — ejected cartridge cases.
 * owner: fx
 *
 * Real lathed brass (rim, tapered body, thinned case mouth) so the silhouette
 * reads at 40 cm from the camera, instanced into a single draw call, with a
 * per-instance heat term that fades the emissive from chamber-hot to cold brass.
 *
 * Simulation uses ctx.physics.addBody when the physics team's rigid bodies are
 * available, and falls back to a local integrator (gravity + restitution +
 * angular momentum, colliding via ctx.physics.raycast or the world floor).
 */
import * as THREE from 'three';

function caseGeometry() {
  const R = 0.00475, L = 0.0195;
  const pts = [
    new THREE.Vector2(0.0, 0.0),
    new THREE.Vector2(R * 0.94, 0.0),
    new THREE.Vector2(R * 1.06, 0.0006),
    new THREE.Vector2(R * 1.07, 0.0016),
    new THREE.Vector2(R * 0.97, 0.0026),
    new THREE.Vector2(R * 0.99, L * 0.55),
    new THREE.Vector2(R * 0.965, L * 0.94),
    new THREE.Vector2(R * 0.95, L),
    new THREE.Vector2(R * 0.80, L),
    new THREE.Vector2(R * 0.80, L * 0.35),
    new THREE.Vector2(0.0, L * 0.30),
  ];
  const g = new THREE.LatheGeometry(pts, 14);
  g.translate(0, -L * 0.45, 0);
  g.computeVertexNormals();
  return g;
}

export class ShellSystem {
  constructor(ctx, capacity = 32) {
    this.capacity = capacity;
    this.ctx = ctx;
    this.head = 0;

    const geo = caseGeometry();
    const mat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(0.62, 0.44, 0.16),
      metalness: 1.0,
      roughness: 0.30,
      emissive: new THREE.Color(0, 0, 0),
    });
    this.heat = new Float32Array(capacity);
    const heatAttr = new THREE.InstancedBufferAttribute(this.heat, 1).setUsage(THREE.DynamicDrawUsage);
    mat.onBeforeCompile = (shader) => {
      shader.vertexShader = 'attribute float iHeat;\nvarying float vHeat;\n' +
        shader.vertexShader.replace('void main() {', 'void main() {\n\tvHeat = iHeat;');
      shader.fragmentShader = 'varying float vHeat;\n' +
        shader.fragmentShader.replace(
          '#include <emissivemap_fragment>',
          '#include <emissivemap_fragment>\n\ttotalEmissiveRadiance += vec3(4.5, 1.05, 0.10) * vHeat * vHeat;'
        );
    };
    mat.customProgramCacheKey = () => 'fx_shell_heat';

    this.mesh = new THREE.InstancedMesh(geo, mat, capacity);
    this.mesh.geometry.setAttribute('iHeat', heatAttr);
    this.heatAttr = heatAttr;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = true;
    this.mesh.name = 'fx_shells';
    this.mesh.userData.noCollide = true;
    this.mesh.userData.fx = true;
    this.mesh.count = capacity;

    this.state = [];
    for (let i = 0; i < capacity; i++) {
      this.state.push({
        live: false, age: 0, life: 8,
        pos: new THREE.Vector3(), vel: new THREE.Vector3(),
        quat: new THREE.Quaternion(), spin: new THREE.Vector3(),
        rest: false, body: null,
      });
      _m.makeScale(0, 0, 0);
      this.mesh.setMatrixAt(i, _m);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    this.floorY = 0;
  }

  /**
   * @param {THREE.Vector3} pos ejection port world position
   * @param {THREE.Vector3} dir ejection direction (usually the weapon's right + up)
   */
  eject(pos, dir, o = {}) {
    const i = this.head;
    this.head = (this.head + 1) % this.capacity;
    const s = this.state[i];
    if (s.body && this.ctx.physics?.removeBody) this.ctx.physics.removeBody(s.body);
    s.live = true; s.age = 0; s.life = o.life ?? 9; s.rest = false;
    s.pos.copy(pos);
    s.vel.copy(dir).multiplyScalar(o.speed ?? 2.6);
    if (o.inherit) s.vel.add(o.inherit);
    s.quat.setFromEuler(_e.set(
      (o.seed ?? 0.5) * 6.28, (o.seed ?? 0.5) * 3.14, (o.seed ?? 0.5) * 1.7));
    const sp = o.spin ?? 34;
    s.spin.set(
      (((o.seed ?? 0.5) * 7.31) % 1 - 0.5) * sp,
      (((o.seed ?? 0.5) * 3.77) % 1 - 0.5) * sp * 0.6,
      (((o.seed ?? 0.5) * 5.19) % 1 - 0.5) * sp);
    this.heat[i] = 1;

    const phys = this.ctx.physics;
    if (phys && typeof phys.addBody === 'function') {
      try {
        s.body = phys.addBody({
          mesh: null, mass: 0.008, restitution: 0.32, friction: 0.5,
          shape: 'box', size: [0.010, 0.020, 0.010],
          position: s.pos.clone(), velocity: s.vel.clone(),
        }) || null;
      } catch { s.body = null; }
    }
  }

  update(dt, env) {
    const phys = this.ctx.physics;
    const g = 9.81 * 2.2;
    let any = false;
    for (let i = 0; i < this.capacity; i++) {
      const s = this.state[i];
      if (!s.live) continue;
      any = true;
      s.age += dt;
      if (s.age > s.life) {
        s.live = false;
        if (s.body && phys?.removeBody) { phys.removeBody(s.body); s.body = null; }
        _m.makeScale(0, 0, 0);
        this.mesh.setMatrixAt(i, _m);
        this.heat[i] = 0;
        continue;
      }
      this.heat[i] = Math.max(0, 1 - s.age / 1.5) ** 1.6;

      if (s.body && s.body.position) {
        s.pos.copy(s.body.position);
        if (s.body.quaternion) s.quat.copy(s.body.quaternion);
      } else if (!s.rest) {
        s.vel.y -= g * dt;
        s.vel.multiplyScalar(Math.exp(-0.25 * dt));
        _p.copy(s.pos).addScaledVector(s.vel, dt);

        let hit = null;
        const move = _p.distanceTo(s.pos);
        if (phys?.raycast && move > 1e-5) {
          _d.subVectors(_p, s.pos).multiplyScalar(1 / move);
          try { hit = phys.raycast(s.pos, _d, move + 0.012); } catch { hit = null; }
        }
        const hitPoint = hit?.point || null;
        const hitNormal = hit?.normal || hit?.face?.normal || null;
        if (hitPoint && hitNormal) {
          s.pos.copy(hitPoint).addScaledVector(hitNormal, 0.006);
          const vn = s.vel.dot(hitNormal);
          if (vn < 0) {
            _t.copy(s.vel).addScaledVector(hitNormal, -vn);
            s.vel.copy(_t).multiplyScalar(0.62).addScaledVector(hitNormal, -vn * 0.34);
            s.spin.multiplyScalar(0.55);
          }
          if (s.vel.lengthSq() < 0.09) { s.vel.set(0, 0, 0); s.spin.set(0, 0, 0); s.rest = true; }
        } else if (_p.y <= this.floorY + 0.005) {
          s.pos.set(_p.x, this.floorY + 0.005, _p.z);
          if (s.vel.y < 0) {
            s.vel.y = -s.vel.y * 0.34;
            s.vel.x *= 0.6; s.vel.z *= 0.6;
            s.spin.multiplyScalar(0.5);
          }
          if (Math.abs(s.vel.y) < 0.25 && s.vel.lengthSq() < 0.12) {
            s.vel.set(0, 0, 0); s.spin.set(0, 0, 0); s.rest = true;
          }
        } else {
          s.pos.copy(_p);
        }

        const w = s.spin.length();
        if (w > 1e-4) {
          _q.setFromAxisAngle(_t.copy(s.spin).multiplyScalar(1 / w), w * dt);
          s.quat.premultiply(_q);
        }
      }

      const fade = s.age > s.life - 0.6 ? Math.max(0, (s.life - s.age) / 0.6) : 1;
      _m.compose(s.pos, s.quat, _sc.set(fade, fade, fade));
      this.mesh.setMatrixAt(i, _m);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    this.heatAttr.needsUpdate = true;
    this.mesh.visible = any;
    if (env?.floorY !== undefined) this.floorY = env.floorY;
  }

  reset() {
    for (let i = 0; i < this.capacity; i++) {
      const s = this.state[i];
      if (s.body && this.ctx.physics?.removeBody) this.ctx.physics.removeBody(s.body);
      s.body = null; s.live = false;
      this.heat[i] = 0;
      _m.makeScale(0, 0, 0);
      this.mesh.setMatrixAt(i, _m);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    this.heatAttr.needsUpdate = true;
    this.head = 0;
  }

  dispose() { this.mesh.geometry.dispose(); this.mesh.material.dispose(); }
}

const _m = new THREE.Matrix4();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _d = new THREE.Vector3();
const _t = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _sc = new THREE.Vector3(1, 1, 1);
